import { useState, useEffect, useMemo } from "react";
import { profileApi } from "../api/client";
import { C, fmt, Card, Tag, ProgressBar } from "./theme";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";

// Allocation templates — percentages must sum to 100
const ALLOC_TEMPLATES = {
  conservative: [
    { name: "Emergency Fund",      pct: 30, color: C.red,     desc: "Liquid MF / high-yield savings" },
    { name: "Fixed Deposits",      pct: 25, color: C.gold,    desc: "Bank FD (7-8%)" },
    { name: "Debt Mutual Funds",   pct: 20, color: C.blue,    desc: "Short duration, govt securities" },
    { name: "Equity Mutual Funds", pct: 15, color: C.success, desc: "Hybrid + balanced advantage" },
    { name: "Gold (SGB/ETF)",      pct:  5, color: C.gold,    desc: "Sovereign Gold Bond" },
    { name: "Direct Stocks",       pct:  5, color: C.purple,  desc: "Blue-chip dividend payers" },
  ],
  moderate: [
    { name: "Emergency Fund",      pct: 10, color: C.red,     desc: "Liquid MF / savings" },
    { name: "Fixed Deposits",      pct: 15, color: C.gold,    desc: "Bank FD (7-8%)" },
    { name: "Debt Mutual Funds",   pct: 15, color: C.blue,    desc: "Short duration" },
    { name: "Equity Mutual Funds", pct: 30, color: C.success, desc: "Index + ELSS SIP" },
    { name: "Gold (SGB/ETF)",      pct: 10, color: C.gold,    desc: "Sovereign Gold Bond" },
    { name: "Direct Stocks",       pct: 20, color: C.purple,  desc: "Blue-chip + growth" },
  ],
  aggressive: [
    { name: "Emergency Fund",      pct:  5, color: C.red,     desc: "Liquid MF / savings" },
    { name: "Fixed Deposits",      pct: 10, color: C.gold,    desc: "Short-term FD only" },
    { name: "Debt Mutual Funds",   pct: 10, color: C.blue,    desc: "Tactical allocation" },
    { name: "Equity Mutual Funds", pct: 40, color: C.success, desc: "Flexi-cap, mid-cap, small-cap" },
    { name: "Gold (SGB/ETF)",      pct: 10, color: C.gold,    desc: "Sovereign Gold Bond" },
    { name: "Direct Stocks",       pct: 25, color: C.purple,  desc: "Growth + sector themes" },
  ],
};

// Months of expenses to hold as emergency fund, by risk profile
const EMERGENCY_MONTHS = { conservative: 9, moderate: 6, aggressive: 3 };

const HORIZONS = {
  conservative: [
    { label: "Short-term (1-3 yrs)", color: C.blue,    icon: "⚡", items: ["Liquid Mutual Funds", "Short-term FDs (1-2 yr)", "Savings accounts"] },
    { label: "Mid-term (3-7 yrs)",   color: C.gold,    icon: "🎯", items: ["Corporate Bond Funds", "Hybrid Conservative Funds", "PPF, Sukanya Samriddhi"] },
    { label: "Long-term (7+ yrs)",   color: C.accent,  icon: "🌱", items: ["Index Funds (limited)", "NPS Tier I (low equity)", "Tax-free bonds"] },
  ],
  moderate: [
    { label: "Short-term (1-3 yrs)", color: C.blue,    icon: "⚡", items: ["Liquid Mutual Funds", "Short-term FDs (1-2 yr)", "Arbitrage Funds"] },
    { label: "Mid-term (3-7 yrs)",   color: C.gold,    icon: "🎯", items: ["Balanced Advantage Funds", "Corporate Bond Funds", "PPF partial"] },
    { label: "Long-term (7+ yrs)",   color: C.accent,  icon: "🌱", items: ["ELSS + Index Funds", "NPS Tier I & II", "Direct bluechips"] },
  ],
  aggressive: [
    { label: "Short-term (1-3 yrs)", color: C.blue,    icon: "⚡", items: ["Liquid Mutual Funds (parking)", "Arbitrage Funds"] },
    { label: "Mid-term (3-7 yrs)",   color: C.gold,    icon: "🎯", items: ["Flexi-cap MFs", "Mid-cap MFs", "Themed Funds"] },
    { label: "Long-term (7+ yrs)",   color: C.accent,  icon: "🌱", items: ["Small-cap MFs", "Direct stocks (growth)", "International ETFs", "NPS Tier I (75% equity)"] },
  ],
};

const SECTORS = [
  { s: "IT & Technology",     p: 25, eg: "TCS, Infosys",     c: C.blue },
  { s: "Banking & Finance",   p: 20, eg: "HDFC, ICICI",      c: C.gold },
  { s: "Healthcare",          p: 15, eg: "Sun Pharma",        c: C.success },
  { s: "FMCG & Consumer",     p: 15, eg: "HUL, ITC",          c: C.accent },
  { s: "Infrastructure",      p: 10, eg: "L&T, PowerGrid",    c: C.purple },
  { s: "Energy & Green",      p: 10, eg: "Reliance, Adani",   c: C.orange },
  { s: "Metals & Mining",     p:  5, eg: "Tata Steel",        c: "#94a3b8" },
  { s: "Gold & Commodities",  p:  5, eg: "SGB, Gold ETF",     c: "#fbbf24" },
];

const DEFAULT_PREFS = {
  riskProfile: "moderate",
  monthlyInvestable: 0,
  autoCalculate: true,
  existingEmergencyFund: 0,
  essentialMonthlyOverride: 0,
};

export default function InvestmentsScreen({ monthlyIncome = 0, monthlyExpense = 0, monthlyEssentialExpense = 0, monthlySavings = 0 }) {
  const [prefs, setPrefs]                 = useState(DEFAULT_PREFS);
  const [savedPrefs, setSavedPrefs]       = useState(DEFAULT_PREFS);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [holdings, setHoldings]           = useState([]);
  const [holdingsLoaded, setHoldingsLoaded] = useState(false);
  const [editingIsin, setEditingIsin]     = useState(null);
  const [editDraft, setEditDraft]         = useState({ quantity: "", sector: "" });
  const [holdingBusy, setHoldingBusy]     = useState(false);
  const [showAddForm, setShowAddForm]     = useState(false);
  const [addForm, setAddForm]             = useState({ isin: "", companyName: "", sector: "Other", quantity: "" });

  const ALL_SECTORS = ["IT", "Banking", "Finance", "Insurance", "Energy", "Healthcare", "FMCG", "Auto", "Telecom", "Metals", "Cement", "Real Estate", "Internet", "Conglomerate", "Infrastructure", "Other"];

  const addHoldingManually = async () => {
    setHoldingBusy(true);
    try {
      await profileApi.createHolding({
        ...addForm,
        isin: addForm.isin.trim().toUpperCase(),
        quantity: Number(addForm.quantity),
      });
      const r = await profileApi.getHoldings();
      setHoldings(r.holdings || []);
      setAddForm({ isin: "", companyName: "", sector: "Other", quantity: "" });
      setShowAddForm(false);
    } catch (e) { alert(e.message); }
    finally { setHoldingBusy(false); }
  };

  const startEditHolding = (h) => {
    setEditingIsin(h.isin);
    setEditDraft({ quantity: h.quantity, sector: h.sector });
  };

  const saveHolding = async (isin) => {
    setHoldingBusy(true);
    try {
      const q = Number(editDraft.quantity);
      if (isNaN(q) || q < 0) throw new Error("Invalid quantity");
      await profileApi.updateHolding(isin, { quantity: q, sector: editDraft.sector });
      const r = await profileApi.getHoldings();
      setHoldings(r.holdings || []);
      setEditingIsin(null);
    } catch (e) { alert(e.message); }
    finally { setHoldingBusy(false); }
  };

  const deleteHolding = async (h) => {
    if (!window.confirm(`Delete ${h.companyName.slice(0, 60)}? This removes it from your holdings.`)) return;
    setHoldingBusy(true);
    try {
      await profileApi.deleteHolding(h.isin);
      setHoldings(prev => prev.filter(x => x.isin !== h.isin));
    } catch (e) { alert(e.message); }
    finally { setHoldingBusy(false); }
  };

  // Use override if > 0, else auto-detect from essential categories
  const effectiveEssential = prefs.essentialMonthlyOverride > 0
    ? prefs.essentialMonthlyOverride
    : monthlyEssentialExpense;
  const discretionary = Math.max(0, monthlyExpense - monthlyEssentialExpense);

  // Detect unsaved changes
  const isDirty = JSON.stringify(prefs) !== JSON.stringify(savedPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    profileApi.getInvestments()
      .then(p => {
        const merged = { ...DEFAULT_PREFS, ...p };
        setPrefs(merged);
        setSavedPrefs(merged);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    profileApi.getHoldings()
      .then(d => setHoldings(d.holdings || []))
      .catch(() => {})
      .finally(() => setHoldingsLoaded(true));
  }, []);

  // Derived: emergency fund math — based on ESSENTIAL expenses only (needs, not wants)
  const isDeficit         = monthlySavings <= 0;
  const emergencyMonths   = EMERGENCY_MONTHS[prefs.riskProfile];
  const emergencyTarget   = effectiveEssential * emergencyMonths;
  const emergencyHave     = Math.min(prefs.existingEmergencyFund, emergencyTarget);
  const emergencyGap      = Math.max(0, emergencyTarget - prefs.existingEmergencyFund);
  const emergencyMet      = emergencyGap === 0 && effectiveEssential > 0;
  const emergencyProgress = emergencyTarget > 0 ? (emergencyHave / emergencyTarget) * 100 : 0;

  // Investable amount
  const autoInvestable      = isDeficit ? 0 : Math.round(monthlySavings * 0.8);
  const effectiveInvestable = prefs.autoCalculate ? autoInvestable : Math.max(0, prefs.monthlyInvestable);

  // Months to fill emergency fund at current rate (if not met)
  const monthsToFillEmergency = useMemo(() => {
    if (emergencyMet || effectiveInvestable === 0) return null;
    const tpl   = ALLOC_TEMPLATES[prefs.riskProfile];
    const efPct = tpl.find(a => a.name === "Emergency Fund")?.pct || 0;
    const monthlyToEF = (effectiveInvestable * efPct) / 100;
    if (monthlyToEF === 0) return null;
    return Math.ceil(emergencyGap / monthlyToEF);
  }, [emergencyMet, emergencyGap, effectiveInvestable, prefs.riskProfile]);

  // Smart allocation: redistribute EF % to other categories if EF target is met
  const allocations = useMemo(() => {
    const tpl = ALLOC_TEMPLATES[prefs.riskProfile] || ALLOC_TEMPLATES.moderate;

    if (emergencyMet) {
      const efPct       = tpl.find(a => a.name === "Emergency Fund")?.pct || 0;
      const others      = tpl.filter(a => a.name !== "Emergency Fund");
      const totalOthers = others.reduce((s, a) => s + a.pct, 0);
      return others.map(a => {
        const newPct = (a.pct / totalOthers) * 100;
        return {
          ...a,
          pct: Math.round(newPct),
          amount: Math.round((effectiveInvestable * newPct) / 100),
        };
      });
    }

    return tpl.map(a => ({ ...a, amount: Math.round((effectiveInvestable * a.pct) / 100) }));
  }, [prefs.riskProfile, effectiveInvestable, emergencyMet]);

  const horizons = HORIZONS[prefs.riskProfile] || HORIZONS.moderate;

  const save = async () => {
    setSaving(true); setErr(""); setSavedMsg("");
    try {
      const result = await profileApi.saveInvestments(prefs);
      const merged = { ...DEFAULT_PREFS, ...result };
      setPrefs(merged);
      setSavedPrefs(merged);
      setSavedMsg("Saved ✓");
      setTimeout(() => setSavedMsg(""), 2500);
    }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const resetChanges = () => setPrefs(savedPrefs);

  if (loading) return <Card style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading investment profile...</Card>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Unsaved changes / save status banner */}
      {(isDirty || savedMsg) && (
        <div style={{
          position: "sticky", top: 16, zIndex: 50,
          background: isDirty ? C.gold + "15" : C.success + "15",
          border: `1px solid ${isDirty ? C.gold + "55" : C.success + "55"}`,
          borderRadius: 8, padding: "10px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          flexWrap: "wrap",
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: isDirty ? C.gold : C.success }}>
            {isDirty ? "You have unsaved changes" : savedMsg}
          </div>
          {isDirty && (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={resetChanges}
                style={{ padding: "8px 14px", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 24, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                Discard
              </button>
              <button onClick={save} disabled={saving}
                style={{ padding: "8px 18px", background: C.accent, color: "#fff", border: "none", borderRadius: 24, fontWeight: 600, fontSize: 12, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Deficit banner */}
      {isDeficit && (
        <Card style={{ border: `1px solid ${C.red}55`, background: C.red + "11" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 24 }}>⚠️</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.red }}>Expenses exceed income this month</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
                Income: <strong style={{ color: C.success }}>{fmt(monthlyIncome)}</strong> · Expenses: <strong style={{ color: C.red }}>{fmt(monthlyExpense)}</strong> · Deficit: <strong style={{ color: C.red }}>{fmt(Math.abs(monthlySavings))}</strong>
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.6 }}>
                Auto-calculation is disabled. Reduce expenses or increase income before allocating to investments.
                You can still enter a manual investable amount below if you have other income sources.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Emergency Fund Status — based on ESSENTIAL expenses only */}
      {effectiveEssential > 0 && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>
              🛡️ Emergency Fund Status
            </div>
            {emergencyMet
              ? <Tag c={C.success}>✓ Fully funded</Tag>
              : <Tag c={C.gold}>{Math.round(emergencyProgress)}% funded</Tag>}
          </div>

          {/* Needs vs Wants breakdown */}
          {monthlyExpense > 0 && (
            <div style={{ background: C.surface, borderRadius: 8, padding: 12, marginBottom: 14, border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.5 }}>MONTHLY EXPENSE BREAKDOWN</div>
                <button onClick={() => setShowBreakdown(s => !s)}
                  style={{ background: "transparent", border: "none", color: C.blue, fontSize: 11, fontWeight: 600, cursor: "pointer", padding: 0 }}>
                  {showBreakdown ? "Hide categories" : "What counts as essential?"}
                </button>
              </div>
              <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 10 }}>
                <div style={{ width: `${(monthlyEssentialExpense / monthlyExpense) * 100}%`, background: C.accent }} />
                <div style={{ width: `${(discretionary / monthlyExpense) * 100}%`, background: C.gold }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
                    <span style={{ width: 8, height: 8, background: C.accent, borderRadius: "50%" }} /> Essential (needs)
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginTop: 2 }}>{fmt(monthlyEssentialExpense)}</div>
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
                    <span style={{ width: 8, height: 8, background: C.gold, borderRadius: "50%" }} /> Discretionary (wants)
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.gold, marginTop: 2 }}>{fmt(discretionary)}</div>
                </div>
              </div>
              {showBreakdown && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted, lineHeight: 1.7 }}>
                  <div><strong style={{ color: C.accent }}>Essential</strong> · Housing, Food, Transport, Healthcare, Education, Utilities, EMI/Loan, Insurance</div>
                  <div style={{ marginTop: 4 }}><strong style={{ color: C.gold }}>Discretionary</strong> · Entertainment, Clothing, Personal Care, Other</div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 14 }}>
            <Stat label="Essential monthly" value={fmt(effectiveEssential)} color={C.text} />
            <Stat label={`Target (${emergencyMonths}× essential)`} value={fmt(emergencyTarget)} color={C.gold} />
            <Stat label="You have" value={fmt(prefs.existingEmergencyFund || 0)} color={emergencyMet ? C.success : C.blue} />
            <Stat label={emergencyMet ? "Surplus" : "Gap"}
                  value={emergencyMet
                    ? fmt(Math.max(0, prefs.existingEmergencyFund - emergencyTarget))
                    : fmt(emergencyGap)}
                  color={emergencyMet ? C.success : C.red} />
          </div>

          <ProgressBar value={emergencyHave} max={emergencyTarget || 1} color={emergencyMet ? C.success : C.gold} height={10} />

          <div style={{ marginTop: 12, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
            {emergencyMet
              ? <>✓ Your emergency fund covers <strong style={{ color: C.text }}>{emergencyMonths} months</strong> of essential expenses. The full investable amount is now allocated to growth investments below.</>
              : monthsToFillEmergency
                ? <>At current investable rate, you'll fully fund your emergency reserve in <strong style={{ color: C.text }}>~{monthsToFillEmergency} months</strong>. Priority goes to filling this before equity growth.</>
                : <>Add a monthly investable amount above to start building your emergency fund.</>
            }
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 14 }}>
            <div>
              <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6, display: "block" }}>
                EXISTING EMERGENCY FUND
              </label>
              <input type="number" min="0"
                value={prefs.existingEmergencyFund || ""}
                onChange={e => setPrefs(p => ({ ...p, existingEmergencyFund: Number(e.target.value) || 0 }))}
                placeholder="Amount already saved"
                style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 14, fontFamily: "inherit" }} />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                Liquid MF, savings, or short-term FDs reserved for emergencies.
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6, display: "block" }}>
                ESSENTIAL MONTHLY (OVERRIDE)
              </label>
              <input type="number" min="0"
                value={prefs.essentialMonthlyOverride || ""}
                onChange={e => setPrefs(p => ({ ...p, essentialMonthlyOverride: Number(e.target.value) || 0 }))}
                placeholder={`Auto-detected: ${fmt(monthlyEssentialExpense)}`}
                style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 14, fontFamily: "inherit" }} />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                Override the auto-detected essential amount if it doesn't match reality. Leave 0 to use auto.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Preferences */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: C.text }}>⚙️ Your investment preferences</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 14 }}>
          <div>
            <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6, display: "block" }}>RISK PROFILE</label>
            <select value={prefs.riskProfile} onChange={e => setPrefs(p => ({ ...p, riskProfile: e.target.value }))}
              style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", color: C.text, fontSize: 14, fontFamily: "inherit" }}>
              <option value="conservative">Conservative — capital preservation</option>
              <option value="moderate">Moderate — balanced growth</option>
              <option value="aggressive">Aggressive — high growth, high risk</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6, display: "block" }}>MONTHLY INVESTABLE</label>
            <input type="number" min="0"
              value={prefs.autoCalculate ? "" : prefs.monthlyInvestable}
              disabled={prefs.autoCalculate}
              placeholder={prefs.autoCalculate ? (isDeficit ? "₹0 (deficit)" : `Auto: ${fmt(autoInvestable)}`) : "Enter amount"}
              onChange={e => setPrefs(p => ({ ...p, monthlyInvestable: Number(e.target.value) || 0 }))}
              style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", color: C.text, fontSize: 14, fontFamily: "inherit", opacity: prefs.autoCalculate ? 0.6 : 1 }} />
            <label style={{ fontSize: 12, color: C.muted, marginTop: 8, display: "flex", alignItems: "center", gap: 6, cursor: isDeficit ? "not-allowed" : "pointer", opacity: isDeficit ? 0.5 : 1 }}>
              <input type="checkbox" checked={prefs.autoCalculate && !isDeficit}
                disabled={isDeficit}
                onChange={e => setPrefs(p => ({ ...p, autoCalculate: e.target.checked }))} />
              Auto-calculate (80% of monthly savings)
            </label>
          </div>
        </div>
        {err && <div style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
        {savedMsg && <div style={{ color: C.success, fontSize: 12, marginTop: 10 }}>✓ {savedMsg}</div>}
        <button onClick={save} disabled={saving}
          className="pill-outline-btn" style={{ marginTop: 16, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
          {saving ? "Saving..." : "Save Preferences"}
        </button>
      </Card>

      {/* Allocation donut + hoverable horizon icons */}
      {effectiveInvestable > 0 ? (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>💼 Recommended Allocation — {prefs.riskProfile}</span>
            <Tag c={C.accent}>{fmt(effectiveInvestable)} / month</Tag>
          </div>
          {emergencyMet && (
            <div style={{ fontSize: 11, color: C.success, marginBottom: 12, background: C.success + "11", border: `1px solid ${C.success}33`, borderRadius: 6, padding: "6px 10px" }}>
              ✓ Emergency fund target met — redistributed across growth categories.
            </div>
          )}
          <div className="donut-row">
            <div style={{ height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={allocations} dataKey="pct" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {allocations.map((a, i) => <Cell key={i} fill={a.color} />)}
                  </Pie>
                  <RTooltip
                    formatter={(v, n, p) => [`${v}% · ${fmt(p.payload.amount)}`, p.payload.name]}
                    contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="alloc-legend-grid">
              {allocations.map(a => (
                <div key={a.name} title={a.desc} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: C.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{a.pct}% · {fmt(a.amount)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Time horizons as hoverable icons */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>TIME HORIZONS · hover for instrument suggestions</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {horizons.map(h => (
                <div key={h.label} title={h.items.join(" · ")} className="horizon-chip"
                  style={{ background: h.color + "15", border: `1px solid ${h.color}55`, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, cursor: "help" }}>
                  <span style={{ fontSize: 22 }}>{h.icon}</span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: h.color }}>{h.label}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{h.items.length} instruments</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      ) : (
        <Card style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>💡</div>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>No allocation yet</div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            {isDeficit
              ? "Once your monthly savings turn positive, investment recommendations will appear here."
              : "Set a monthly investable amount above to see your personalised allocation."
            }
          </div>
        </Card>
      )}

      {/* Sector diversification — only show if there's equity allocation */}
      {/* ── Real Demat Holdings (from CDSL emails) ───────────────────────── */}
      {holdingsLoaded && holdings.length > 0 && (() => {
        const totalShares = holdings.reduce((s, h) => s + h.quantity, 0);
        const sectorMap = {};
        for (const h of holdings) {
          const sec = h.sector || "Other";
          if (!sectorMap[sec]) sectorMap[sec] = { shares: 0, stocks: 0 };
          sectorMap[sec].shares += h.quantity;
          sectorMap[sec].stocks += 1;
        }
        const sectorEntries = Object.entries(sectorMap).sort((a, b) => b[1].shares - a[1].shares);
        const sectorColors = [C.blue, C.gold, C.success, C.accent, C.purple, C.orange, "#94a3b8", "#fbbf24", "#f472b6", "#60a5fa", C.red];

        return (
          <>
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>📊 Your Demat Holdings</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <Tag c={C.accent}>{holdings.length} stocks</Tag>
                  <Tag c={C.blue}>{totalShares.toFixed(0)} total shares</Tag>
                  <button onClick={() => setShowAddForm(s => !s)}
                    style={{
                      background: showAddForm ? C.greyDark : C.greyLight,
                      color:      showAddForm ? C.greyOnDark : C.text,
                      border: "none", borderRadius: 24, padding: "5px 14px",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      transition: "background 0.15s",
                    }}>
                    {showAddForm ? "Cancel" : "+ Add Stock"}
                  </button>
                </div>
              </div>

              <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
                Auto-imported from CDSL alerts. Forward emails from <code style={{ background: C.surface, padding: "1px 4px", borderRadius: 3 }}>services@cdslindia.co.in</code> to your TrackFin address to keep this updated.
              </div>

              {showAddForm && (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Add an existing holding manually</div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.6 }}>
                    💡 Use this for shares you already own. Future CDSL credits/debits will be added/subtracted from this base — don't add shares twice.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>ISIN (12 CHARS)</label>
                      <input value={addForm.isin} onChange={e => setAddForm(f => ({ ...f, isin: e.target.value.toUpperCase() }))}
                        placeholder="INE009A01021"
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "monospace" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>COMPANY NAME</label>
                      <input value={addForm.companyName} onChange={e => setAddForm(f => ({ ...f, companyName: e.target.value }))}
                        placeholder="INFOSYS LIMITED"
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>SECTOR</label>
                      <select value={addForm.sector} onChange={e => setAddForm(f => ({ ...f, sector: e.target.value }))}
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }}>
                        {ALL_SECTORS.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>QUANTITY</label>
                      <input type="number" step="any" value={addForm.quantity}
                        onChange={e => setAddForm(f => ({ ...f, quantity: e.target.value }))}
                        placeholder="100"
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12, textAlign: "right" }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button onClick={addHoldingManually}
                      disabled={holdingBusy || !addForm.isin || !addForm.companyName || !addForm.quantity}
                      style={{ padding: "8px 18px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: "pointer", opacity: (holdingBusy || !addForm.isin || !addForm.companyName || !addForm.quantity) ? 0.5 : 1 }}>
                      {holdingBusy ? "Adding..." : "Add Holding"}
                    </button>
                    <button onClick={() => setShowAddForm(false)}
                      style={{ padding: "8px 18px", background: C.greyLight, color: C.text, border: "none", borderRadius: 24, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div style={{ maxHeight: 380, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
                {holdings.map((h, i) => {
                  const isEditing = editingIsin === h.isin;
                  return (
                    <div key={h.isin} style={{ padding: "10px 14px", borderBottom: i < holdings.length - 1 ? `1px solid ${C.border}` : "none", background: i % 2 ? C.surface : "transparent" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div style={{ fontSize: 13, color: C.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.companyName}</div>
                          <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{h.isin}</div>
                        </div>

                        {!isEditing ? (
                          <>
                            <Tag c={C.purple}>{h.sector}</Tag>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, minWidth: 50, textAlign: "right" }}>
                              {h.quantity.toFixed(h.quantity % 1 === 0 ? 0 : 2)}
                            </div>
                            <button onClick={() => startEditHolding(h)} disabled={holdingBusy}
                              style={{ background: C.greyLight, border: "none", borderRadius: 24, padding: "4px 12px", fontSize: 11, color: C.text, cursor: "pointer", fontWeight: 600 }}>
                              Edit
                            </button>
                            <button onClick={() => deleteHolding(h)} disabled={holdingBusy}
                              style={{ background: C.greyLight, border: "none", borderRadius: 24, padding: "4px 10px", fontSize: 11, color: C.text, cursor: "pointer", fontWeight: 600 }}>
                              🗑
                            </button>
                          </>
                        ) : (
                          <>
                            <select value={editDraft.sector} onChange={e => setEditDraft(d => ({ ...d, sector: e.target.value }))}
                              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 8px", color: C.text, fontSize: 12 }}>
                              {ALL_SECTORS.map(s => <option key={s}>{s}</option>)}
                            </select>
                            <input type="number" step="any" value={editDraft.quantity}
                              onChange={e => setEditDraft(d => ({ ...d, quantity: e.target.value }))}
                              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 8px", color: C.text, fontSize: 12, width: 80, textAlign: "right" }} />
                            <button onClick={() => saveHolding(h.isin)} disabled={holdingBusy}
                              style={{ background: C.accent, border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 11, color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                              {holdingBusy ? "..." : "✓ Save"}
                            </button>
                            <button onClick={() => setEditingIsin(null)} disabled={holdingBusy}
                              style={{ background: C.greyLight, border: "none", borderRadius: 24, padding: "5px 14px", fontSize: 11, color: C.text, cursor: "pointer", fontWeight: 600 }}>
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: C.text }}>🌐 Actual Sector Diversification</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>Computed from your real demat holdings (by share count — value-weighted view coming with price API)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                {sectorEntries.map(([sector, data], i) => {
                  const color   = sectorColors[i % sectorColors.length];
                  const pctShares = Math.round((data.shares / totalShares) * 100);
                  return (
                    <div key={sector} style={{ background: C.surface, borderRadius: 10, padding: 12, border: `1px solid ${color}33` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color }}>{sector}</span>
                        <Tag c={color}>{pctShares}%</Tag>
                      </div>
                      <ProgressBar value={pctShares} max={50} color={color} />
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 5 }}>{data.stocks} stocks · {data.shares.toFixed(0)} shares</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </>
        );
      })()}

      {/* Sector gap analysis — actual vs recommended */}
      {holdings.length > 0 && (() => {
        const totalShares = holdings.reduce((s, h) => s + h.quantity, 0);
        const actualPct = {};
        for (const h of holdings) {
          const s = h.sector || "Other";
          actualPct[s] = (actualPct[s] || 0) + h.quantity;
        }
        Object.keys(actualPct).forEach(k => { actualPct[k] = (actualPct[k] / totalShares) * 100; });
        const gaps = SECTORS.map(s => ({
          name: s.s,
          color: s.c,
          recommended: s.p,
          actual: Math.round(actualPct[s.s] || 0),
          gap: Math.round((actualPct[s.s] || 0) - s.p),
        })).sort((a, b) => a.gap - b.gap);
        return (
          <Card>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: C.text }}>🎯 Sector Gap Analysis</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>Where you're underweight (need more) vs recommended target. Based on share count from your demat holdings.</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
              {gaps.map(g => {
                const isShort  = g.gap < -2;
                const isOver   = g.gap > 2;
                const status   = isShort ? "underweight" : isOver ? "overweight" : "on target";
                const color    = isShort ? C.red : isOver ? C.gold : C.success;
                return (
                  <div key={g.name} style={{ padding: 10, background: C.surface, borderRadius: 8, border: `1px solid ${color}33` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: g.color }}>{g.name}</span>
                      <Tag c={color}>{status}</Tag>
                    </div>
                    <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
                      <div style={{ flex: 1 }}>
                        <ProgressBar value={g.actual} max={Math.max(g.actual, g.recommended) + 5} color={color} height={6} />
                      </div>
                      <span style={{ fontSize: 11, color: C.muted, minWidth: 56, textAlign: "right" }}>
                        {g.actual}% / {g.recommended}%
                      </span>
                    </div>
                    {g.gap !== 0 && (
                      <div style={{ fontSize: 10, color: C.muted }}>
                        {isShort ? `↑ Increase by ${Math.abs(g.gap)}%` : isOver ? `↓ Reduce by ${g.gap}%` : ""}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}

      {effectiveInvestable > 0 && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: C.text }}>🌐 Recommended Sector Targets (Equity portion)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
            {SECTORS.map(s => (
              <div key={s.s} style={{ background: C.surface, borderRadius: 10, padding: 12, border: `1px solid ${s.c}33` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: s.c }}>{s.s}</span>
                  <Tag c={s.c}>{s.p}%</Tag>
                </div>
                <ProgressBar value={s.p} max={30} color={s.c} />
                <div style={{ fontSize: 10, color: C.muted, marginTop: 5 }}>{s.eg}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}
